import { Button, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCopy, IconFilterOff, IconPencil, IconSearch, IconTrash } from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ContactFilters } from 'src/Components/Contacts/ContactFilters';
import { ContactList } from 'src/Components/Contacts/ContactList';
import { BlockedContactDeletionModal } from 'src/Components/Modals/BlockedContactDeletionModal';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { FavoriteButton } from 'src/Components/Shared/FavoriteButton';
import { RowAction, RowActions } from 'src/Components/Shared/RowActions';
import { SelectBanner } from 'src/Components/Shared/SelectBanner';
import { ListedContact, useFormatListedContacts } from 'src/Hooks/Format/useFormatListedContacts';
import useDuplicateContactModal from 'src/Hooks/Modals/useDuplicateContactModal';
import { useCreateContactExport } from 'src/Hooks/Mutations/useCreateContactExport';
import { useDeleteContact } from 'src/Hooks/Mutations/useDeleteContact';
import { useToggleFavorite } from 'src/Hooks/Mutations/useToggleFavorite';
import { useFetchContactDeletionCheck } from 'src/Hooks/Queries/useFetchContactDeletionCheck';
import useFetchContacts from 'src/Hooks/Queries/useFetchContacts';
import useContactFilters from 'src/Hooks/Shared/useContactFilters';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { contactExportNameGenerator } from 'src/Services/Formatters/contactExportNameGenerator';

import styles from './AssetContactList.module.scss';

const AssetContactList = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search.trim(), 400);
  const { data: contacts, isLoading } = useFetchContacts(debouncedSearch);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();

  const formattedContacts = useFormatListedContacts(contacts);
  const {
    favoritesOnly,
    emergencyOnly,
    selectedCategories,
    setFavoritesOnly,
    setEmergencyOnly,
    setSelectedCategories,
    resetFilters: resetCategoryFilters,
    hasActiveFilters: hasCategoryFilters,
  } = useContactFilters();
  const hasActiveFilters = hasCategoryFilters || debouncedSearch.length > 0;
  const resetFilters = () => {
    setSearch('');
    resetCategoryFilters();
  };

  const visibleContacts = useMemo(() => {
    if (!formattedContacts) {
      return undefined;
    }
    return formattedContacts.filter(
      contact =>
        (!favoritesOnly || contact.isFavorite) &&
        (!emergencyOnly || contact.isEmergencyContact) &&
        (selectedCategories.length === 0 ||
          (contact.category !== undefined && selectedCategories.includes(contact.category)))
    );
  }, [formattedContacts, favoritesOnly, emergencyOnly, selectedCategories]);

  const [exportName, setExportName] = useState<string>('');

  const [contactToDelete, setContactToDelete] = useState<ListedContact | null>(null);
  const { data: deletionCheck } = useFetchContactDeletionCheck(contactToDelete?.id ?? null);

  const { mutate: deleteContact } = useDeleteContact({
    onSuccess: () => setContactToDelete(null),
  });

  const contactToDeleteName = contactToDelete?.name ?? '';

  useEffect(() => {
    if (contactToDelete && deletionCheck?.canDelete === true) {
      deleteContact(contactToDelete.id);
    }
  }, [contactToDelete, deletionCheck?.canDelete, deleteContact]);
  const { mutate: toggleFavorite } = useToggleFavorite();
  const {
    mutate: exportContacts,
    isLoading: exportLoading,
    data: exportedInfo,
  } = useCreateContactExport({
    onSuccess: data => {
      setShowQRCodeModal(true);
      setExportName(contactExportNameGenerator(data, t));
    },
  });
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);

  const [selectedContacts, setSelectedContacts] = useState<ListedContact[]>([]);

  const [selectBannerVisible, setSelectBannerVisible] = useState(false);

  useEffect(() => {
    const visible = selectedContacts.length > 0;
    setSelectBannerVisible(visible);
  }, [selectedContacts.length]);

  const { t } = useTranslation();

  const openDuplicateModal = useDuplicateContactModal();

  const assetAboveQuotas = useIsAssetAboveQuotas();

  const extraColumns: DataTableColumn<ListedContact>[] = [
    {
      accessor: 'isFavorite',
      title: '',
      width: 30,
      hidden: isMobile,
      render: ({ isFavorite, id }) => (
        <FavoriteButton
          disabled={assetAboveQuotas}
          isFavorite={isFavorite}
          onChange={(value, e) => {
            e.stopPropagation();
            toggleFavorite({ contactId: id, isFavorite: value });
          }}
        />
      ),
    },
    {
      accessor: 'actions',
      title: '',
      width: 130,
      hidden: isMobile,
      render: ({ id, name }) => {
        const actions: RowAction[] = [
          {
            key: 'edit',
            label: t('contacts.table.menu.edit', 'Modifier'),
            icon: <IconPencil size={18} />,
            disabled: assetAboveQuotas,
            onClick: () => navigate(`${id}/edit`),
          },
          {
            key: 'duplicate',
            label: t('contacts.table.menu.duplicate', 'Dupliquer'),
            icon: <IconCopy size={18} />,
            onClick: () => openDuplicateModal(id),
          },
          {
            key: 'delete',
            label: t('contacts.table.menu.delete', 'Supprimer'),
            icon: <IconTrash size={18} />,
            variant: 'danger',
            onClick: () => {
              const contact = formattedContacts?.find(c => c.id === id);
              if (contact) setContactToDelete(contact);
            },
            confirm: {
              title: t('contacts.delete.confirmTitle', 'Supprimer ce contact ?'),
              message: t(
                'contacts.delete.confirmMessage',
                'Voulez-vous vraiment supprimer {{name}} ? Cette action est irréversible.',
                { name }
              ),
            },
          },
        ];

        return <RowActions actions={actions} />;
      },
      textAlign: 'right',
    },
  ];

  return (
    <div className={styles.container}>
      {selectBannerVisible && (
        <div className={styles.bannerWrapper} id="contactSelectionBanner">
          <SelectBanner
            count={selectedContacts.length}
            exportLoading={exportLoading}
            onExportButtonClick={() => exportContacts(selectedContacts.map(contact => contact.id))}
          />
        </div>
      )}
      <QRCodeModal
        open={showQRCodeModal}
        url={exportedInfo?.publicUrl ?? ''}
        onClose={() => setShowQRCodeModal(false)}
        exportName={exportName}
      />
      <TextInput
        className={styles.search}
        leftSection={<IconSearch size={16} />}
        placeholder={t(
          'contacts.searchPlaceholder',
          'Rechercher un nom, une entreprise, un téléphone, un n° client…'
        )}
        aria-label={t('contacts.searchLabel', 'Rechercher un contact')}
        value={search}
        onChange={event => setSearch(event.currentTarget.value)}
      />
      <ContactFilters
        favoritesOnly={favoritesOnly}
        emergencyOnly={emergencyOnly}
        selectedCategories={selectedCategories}
        setFavoritesOnly={setFavoritesOnly}
        setEmergencyOnly={setEmergencyOnly}
        setSelectedCategories={setSelectedCategories}
        resultCount={hasActiveFilters ? (visibleContacts?.length ?? 0) : undefined}
      />
      {hasActiveFilters && !isLoading && visibleContacts?.length === 0 ? (
        <div className={styles.emptyFiltered}>
          <IconFilterOff size={48} className={styles.emptyIcon} />
          <Text className={styles.emptyText}>
            {t('contacts.filters.emptyResults', 'Aucun contact ne correspond à votre recherche.')}
          </Text>
          <Button variant="subtle" onClick={resetFilters}>
            {t('contacts.filters.reset', 'Réinitialiser les filtres')}
          </Button>
        </div>
      ) : (
        <ContactList
          onContactClick={contact => navigate(contact.id)}
          contacts={visibleContacts}
          selectedContacts={selectedContacts}
          setSelectedContacts={setSelectedContacts}
          rightColumns={extraColumns}
          fetching={isLoading}
        />
      )}
      <BlockedContactDeletionModal
        open={!!contactToDelete && deletionCheck?.canDelete === false}
        onClose={() => setContactToDelete(null)}
        contactName={contactToDeleteName}
        blockers={deletionCheck?.blockers ?? []}
        assetId={assetId ?? ''}
      />
    </div>
  );
};

export default AssetContactList;
